import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { useDebouncedCallback } from "use-debounce";
import { useHistory } from "react-router-dom";
import StickyHeaderDataTable from "../shared/sticky-header-data-table";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import moment from "moment-timezone";

import axios from "axios";
import ai from "../../helpers/axios";
import PageHeaderRowSearch from "../shared/page-header-row-search";
import Loader from "../loader";
import Search from "../search";
import shortenName from "../../helpers/shorten-name";
import { tripTypeConfig, FET_TRIP_TYPE_LABELS, columns } from "./signed-contracts-list-columns";

const reducer = (state, action) => {
  switch (action.type) {
    case "dataBeganDownloading": {
      return {
        ...state,
        loading: true,
        selectedRowsIDs: [],
      };
    }

    case "dataDownloaded": {
      return {
        ...state,
        loading: false,
        data: action?.payload?.data?.data || [],
        totalLength: action?.payload?.data?.countTotal,
      };
    }

    case "search": {
      return {
        ...state,
        query: action.query,
        currentPage: 1,
      };
    }

    case "pageChanged": {
      return {
        ...state,
        currentPage: action.payload.page,
      };
    }

    case "rowsPerPageChanged": {
      return {
        ...state,
        countPerPage: action.payload.currentRowsPerPage,
        currentPage: 1,
      };
    }

    case "tableSort": {
      return {
        ...state,
        sortBy: action.payload.sortBy,
        sortDirection: action.payload.sortDirection,
        currentPage: 1,
      };
    }

    case "selectRows": {
      return {
        ...state,
        selectedRowsIDs: action.selectedRowsIDs,
        data: [...state.data],
      };
    }

    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    default:
      break;
  }
  return state;
};

const initialState = {
  loading: true,
  data: [],
  totalLength: 0,
  query: "",
  countPerPage: 25,
  currentPage: 1,
  sortBy: "signedAt",
  sortDirection: "desc",
  selectedRowsIDs: [],
  lastSelectedRowId: null,
};

const SignedContractsListContent = (props) => {
  const history = useHistory();
  const { user } = useAuth0();

  const userRoles = user && user["https://new.evojets.com/roles"];

  const vp =
    userRoles.some((role) => role === "Account Director") &&
    user?.["https://new.evojets.com/appMetadata"]?.isVP === true;

  const superAdminOrVP =
    userRoles.some(function (role) {
      return "Super Admin" === role;
    }) || vp;

  useEffect(() => {
    if (!superAdminOrVP) {
      toast.error("You do not have permission to view this page.");
      history.push("/");
    }
  }, [superAdminOrVP, history]);

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [state, dispatch] = useReducer(reducer, initialState);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    document.title = "Signed Contracts | evoJets HQ";
  }, []);

  const getData = useCallback(
    async (params) => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      dispatch({ type: "dataBeganDownloading" });

      try {
        const token = await getAccessTokenSilently().catch(loginWithRedirect);

        const { data } = await ai.auth(token).get(`/api/trips/signed-contracts`, {
          signal,
          params,
        });

        dispatch({
          type: "dataDownloaded",
          payload: {
            data,
          },
        });
      } catch (error) {
        if (axios.isCancel(error)) return;
        console.error(error);
      }
    },
    [getAccessTokenSilently, loginWithRedirect]
  );

  const handlePageChange = (page, totalRows) => {
    dispatch({
      type: "pageChanged",
      payload: {
        page,
        totalRows,
      },
    });
  };

  const handleRowsPerPageChange = (currentRowsPerPage, currentPage) => {
    dispatch({
      type: "rowsPerPageChanged",
      payload: {
        currentRowsPerPage,
        currentPage,
      },
    });
  };

  const handleSort = (column, _) => {
    const newDirection =
      column.selector === state.sortBy
        ? state.sortDirection === "asc"
          ? "desc"
          : "asc"
        : "desc";

    dispatch({
      type: "tableSort",
      payload: {
        sortBy: column.selector,
        sortDirection: newDirection,
      },
    });
  };

  const debouncedGetData = useDebouncedCallback((params) => {
    getData(params);
  }, 500);

  const handleSearchInputChange = (e) => {
    let query = e.target.value.toLowerCase();
    dispatch({ type: "search", query });
  };

  useEffect(() => {
    debouncedGetData({
      query: state.query,
      page: state.currentPage,
      countPerPage: state.countPerPage,
      sortBy: state.sortBy,
      sortDirection: state.sortDirection,
    });
  }, [
    debouncedGetData,
    state.query,
    state.currentPage,
    state.countPerPage,
    state.sortBy,
    state.sortDirection,
  ]);

  const dataColumns = useMemo(
    () =>
      columns.map((config) =>
        config.selector === "tripMatch"
          ? {
              ...config,
              cell: (row) => {
                return (
                  <span
                    onClick={() => {
                      dispatch({ type: "search", query: row.tripTag });
                    }}
                    style={{
                      cursor: "pointer",
                      color: row.tripMatch ? "red" : "inherit",
                      fontWeight: row.tripMatch ? "bold" : "normal",
                    }}
                  >
                    {row.tripMatch ? "Yes" : "No"}
                  </span>
                );
              },
            }
          : config
      ),
    []
  );

  const conditionalRowStyles = useMemo(
    () => [
      {
        when: (row) => row.tripTypeHighlight === true,
        style: { backgroundColor: "#fff9c4" },
      },
    ],
    []
  );

  const handleRowClick = useCallback(
    (row, event) => {
      event.preventDefault();
      if (event.shiftKey && state.lastSelectedRowId) {
        window.getSelection().empty();
        const lastClickedIndex = state.data.findIndex(
          (r) => r._id === state.lastSelectedRowId
        );
        const clickedIndex = state.data.findIndex((r) => r._id === row._id);

        const startIndex = Math.min(lastClickedIndex, clickedIndex);
        const endIndex = Math.max(lastClickedIndex, clickedIndex);

        const newSelection = state.data
          .slice(startIndex, endIndex + 1)
          .map((r) => r._id);

        dispatch({
          type: "selectRows",
          selectedRowsIDs: [
            ...new Set([...state.selectedRowsIDs, ...newSelection]),
          ],
        });
      } else {
        dispatch({
          type: "selectRows",
          selectedRowsIDs: state.selectedRowsIDs.find(
            (selectedRowID) => selectedRowID === row._id
          )
            ? [...state.selectedRowsIDs].filter(
                (selectedRowID) => selectedRowID !== row._id
              )
            : [...state.selectedRowsIDs, row._id],
        });
      }

      dispatch({
        type: "fieldChanged",
        field: "lastSelectedRowId",
        value: row._id,
      });
    },
    [state.selectedRowsIDs, state.lastSelectedRowId, state.data]
  );

  const isSelectableRowSelected = useCallback(
    (row) =>
      state.selectedRowsIDs.find((selectedRowID) => selectedRowID === row._id),
    [state.selectedRowsIDs]
  );

  const handleCopySelected = useCallback(async () => {
    const selectedRows = state.data.filter(({ _id }) =>
      state.selectedRowsIDs.includes(_id)
    );

    try {
      await navigator.clipboard.writeText(
        selectedRows
          .map((row) =>
            [
              row.tripTag,
              shortenName(row.ownerName),
              row.signedAt ? moment(row.signedAt).format("M/D/YY") : "",
              row.signedPeriod ? moment(row.signedPeriod).format("M/D/YY") : "",
              row.startDate ? moment(row.startDate).format("M/D/YY") : "",
              row.endDate ? moment(row.endDate).format("M/D/YY") : "",
              row.clientName || "",
              row.tripLegs || "",
              row.clientType || "",
              row.isFetTripType
                ? FET_TRIP_TYPE_LABELS[row.tripType] || row.tripType
                : tripTypeConfig[row.tripType]?.label || "Error",
              row.leadSource || "",
              row.firstContact ? moment(row.firstContact).format("M/D/YY") : "",
              row.routeType || "",
              row.operator || "",
              row.aircraft || "",
            ].join("\t")
          )
          .join("\n")
      );
      toast.success("Data copied to clipboard");
    } catch (err) {}
  }, [state.selectedRowsIDs, state.data]);

  useEffect(() => {
    document.addEventListener("copy", handleCopySelected);

    return () => {
      document.removeEventListener("copy", handleCopySelected);
    };
  }, [handleCopySelected]);

  return (
    <Fragment>
      <PageHeaderRowSearch
        h1="Signed Contracts"
        pretitle="Business"
        breadcrumbs={[{ link: "/signed-contracts", label: "Signed Contracts" }]}
        extraClasses="page-header-trips-l1"
        rightColumn={
          <div id="trip-actions-row">
            <Search
              onSearch={handleSearchInputChange}
              placeholder="Search"
              query={state.query}
            />

            <button
              className="evo-blue base-button"
              disabled={!state.selectedRowsIDs.length}
              onClick={handleCopySelected}
            >
              Copy Selected
            </button>
          </div>
        }
      />

      <div className="server-pagination-table-container trips-list-table-container">
        <StickyHeaderDataTable
          columns={dataColumns}
          data={state.data}
          pagination
          paginationServer
          paginationTotalRows={state.totalLength}
          paginationPerPage={state.countPerPage}
          paginationDefaultPage={state.currentPage}
          paginationRowsPerPageOptions={[25, 50, 100, 150, 200, 250, 500]}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          sortServer
          onSort={handleSort}
          defaultSortFieldId={state.sortBy}
          sortDirection={state.sortDirection}
          progressPending={state.loading}
          progressComponent={<Loader />}
          persistTableHead
          highlightOnHover
          responsive
          onRowClicked={handleRowClick}
          selectableRowSelected={isSelectableRowSelected}
          selectableRowsHighlight
          conditionalRowStyles={conditionalRowStyles}
        />
      </div>
    </Fragment>
  );
};

export default SignedContractsListContent;
